/**
 * ProposalDiscussion Component
 * 
 * Displays a chat-like discussion thread for a proposal with formal action buttons.
 * Supports both client and admin perspectives.
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Send, MessageSquare, CheckCircle, XCircle, AlertCircle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const api = {
  get: (url, token) => axios.get(`${API}${url}`, { headers: { Authorization: `Bearer ${token}` } }),
  post: (url, data, token) => axios.post(`${API}${url}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  put: (url, data, token) => axios.put(`${API}${url}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  delete: (url, token) => axios.delete(`${API}${url}`, { headers: { Authorization: `Bearer ${token}` } }),
};

const ProposalDiscussion = ({ proposal, token, user, lang, onClose, onUpdate }) => {
  const [replies, setReplies] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showActionConfirm, setShowActionConfirm] = useState(null); // 'accept', 'reject', 'request_changes'
  const [actionMessage, setActionMessage] = useState('');
  const chatRef = useRef(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'employee';
  const isClient = user?.role === 'client';

  const t = lang === 'de' ? {
    discussion: 'Diskussion',
    typeMessage: 'Nachricht eingeben...',
    send: 'Senden',
    accept: 'Akzeptieren',
    reject: 'Ablehnen',
    requestChanges: 'Änderungen anfordern',
    acceptProposal: 'Vorschlag akzeptieren',
    rejectProposal: 'Vorschlag ablehnen',
    requestChangesTitle: 'Änderungen anfordern',
    confirmMessage: 'Möchten Sie Ihre Entscheidung kommentieren?',
    commentRequired: 'Bitte geben Sie einen Kommentar ein',
    confirm: 'Bestätigen',
    cancel: 'Abbrechen',
    accepted: 'Akzeptiert',
    rejected: 'Abgelehnt',
    changesRequested: 'Änderungen angefordert',
    internalNote: 'Interne Notiz',
    noReplies: 'Noch keine Antworten',
    proposalClosed: 'Dieser Vorschlag wurde bereits {status}'
  } : {
    discussion: 'Discussion',
    typeMessage: 'Type a message...',
    send: 'Send',
    accept: 'Accept',
    reject: 'Reject',
    requestChanges: 'Request Changes',
    acceptProposal: 'Accept Proposal',
    rejectProposal: 'Reject Proposal',
    requestChangesTitle: 'Request Changes',
    confirmMessage: 'Would you like to add a comment to your decision?',
    commentRequired: 'Please enter a comment',
    confirm: 'Confirm',
    cancel: 'Cancel',
    accepted: 'Accepted',
    rejected: 'Rejected',
    changesRequested: 'Changes Requested',
    internalNote: 'Internal Note',
    noReplies: 'No replies yet',
    proposalClosed: 'This proposal has been {status}'
  };

  useEffect(() => {
    fetchReplies();
    // Poll for new replies every 5 seconds
    const interval = setInterval(fetchReplies, 5000);
    return () => clearInterval(interval);
  }, [proposal.id]);

  useEffect(() => {
    // Scroll to bottom when new replies arrive
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [replies]);

  const fetchReplies = async () => {
    try {
      const res = await api.get(`/proposals/${proposal.id}/replies?include_internal=${isAdmin}`, token);
      setReplies(res.data || []);
    } catch (error) {
      
    }
    setLoading(false);
  };

  const handleSendMessage = async (e, action = null) => {
    e?.preventDefault();
    
    if (!newMessage.trim() && !action) return;
    
    // For reject and request_changes, message is required
    if (action && (action === 'reject' || action === 'request_changes') && !actionMessage.trim()) {
      toast.error(t.commentRequired);
      return;
    }

    setSending(true);
    try {
      const message = action ? actionMessage : newMessage;
      await api.post(
        `/proposals/${proposal.id}/replies`,
        {
          proposal_id: proposal.id,
          message: message,
          is_internal: false,
          action: action
        },
        token
      );
      
      setNewMessage('');
      setActionMessage('');
      setShowActionConfirm(null);
      
      if (action) {
        toast.success(lang === 'de' 
          ? `Vorschlag ${action === 'accept' ? 'akzeptiert' : action === 'reject' ? 'abgelehnt' : 'zur Überarbeitung markiert'}!`
          : `Proposal ${action === 'accept' ? 'accepted' : action === 'reject' ? 'rejected' : 'marked for changes'}!`
        );
        onUpdate?.(); // Trigger parent to refresh
      }
      
      await fetchReplies();
    } catch (error) {
      toast.error('Error sending message');
    }
    setSending(false);
  };

  const handleAction = (action) => {
    setShowActionConfirm(action);
    setActionMessage('');
  };

  const isClosed = ['accepted', 'rejected'].includes(proposal.status);
  const canTakeAction = isClient && !isClosed;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-mmg-mine-shaft flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <MessageSquare size={24} className="text-white" />
              <h2 className="font-heading font-bold text-xl text-white">{t.discussion}</h2>
            </div>
            <p className="text-mmg-silver-chalice text-sm mt-1">{proposal.title}</p>
          </div>
          <button
            onClick={onClose}
            className="text-mmg-silver-chalice hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        {/* Chat Messages */}
        <div
          ref={chatRef}
          className="flex-1 overflow-y-auto p-6 space-y-4"
        >
          {loading ? (
            <div className="text-center text-mmg-silver-chalice">Loading...</div>
          ) : replies.length === 0 ? (
            <div className="text-center text-mmg-silver-chalice py-8">{t.noReplies}</div>
          ) : (
            replies.map((reply) => {
              const isOwnMessage = reply.author_id === user?.id;
              const isInternalNote = reply.is_internal;
              
              return (
                <div
                  key={reply.id}
                  className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] p-4 ${
                      isInternalNote
                        ? 'bg-yellow-900 border border-yellow-700'
                        : isOwnMessage
                        ? 'bg-white text-black'
                        : 'bg-mmg-mine-shaft text-white'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-heading text-sm font-semibold">
                        {reply.author_name}
                      </span>
                      <span className="text-xs text-mmg-boulder">
                        {new Date(reply.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {isInternalNote && (
                        <Lock size={12} className="text-yellow-500" title={t.internalNote} />
                      )}
                    </div>
                    
                    {reply.action && (
                      <div className={`mb-2 inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold ${
                        reply.action === 'accept' ? 'bg-green-600 text-white' :
                        reply.action === 'reject' ? 'bg-red-600 text-white' :
                        'bg-orange-600 text-white'
                      }`}>
                        {reply.action === 'accept' && <><CheckCircle size={14} /> {t.accepted}</>}
                        {reply.action === 'reject' && <><XCircle size={14} /> {t.rejected}</>}
                        {reply.action === 'request_changes' && <><AlertCircle size={14} /> {t.changesRequested}</>}
                      </div>
                    )}
                    
                    <p className="font-body text-sm whitespace-pre-wrap">{reply.message}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Action Confirmation Modal */}
        {showActionConfirm && (
          <div className="absolute inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-10">
            <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 max-w-md w-full">
              <h3 className="font-heading text-lg text-white mb-4">
                {showActionConfirm === 'accept' && t.acceptProposal}
                {showActionConfirm === 'reject' && t.rejectProposal}
                {showActionConfirm === 'request_changes' && t.requestChangesTitle}
              </h3>
              <p className="text-mmg-silver-chalice text-sm mb-4">{t.confirmMessage}</p>
              <textarea
                value={actionMessage}
                onChange={(e) => setActionMessage(e.target.value)}
                placeholder={t.typeMessage}
                className="w-full p-3 bg-black border border-mmg-mine-shaft text-white text-sm min-h-[100px] mb-4"
                required={showActionConfirm !== 'accept'}
              />
              <div className="flex gap-2">
                <button
                  onClick={(e) => handleSendMessage(e, showActionConfirm)}
                  disabled={sending || ((showActionConfirm === 'reject' || showActionConfirm === 'request_changes') && !actionMessage.trim())}
                  className="px-4 py-2 bg-white text-black font-heading text-sm uppercase hover:bg-black hover:text-white border border-white disabled:opacity-50"
                >
                  {t.confirm}
                </button>
                <button
                  onClick={() => { setShowActionConfirm(null); setActionMessage(''); }}
                  className="px-4 py-2 text-mmg-silver-chalice hover:text-white font-heading text-sm uppercase"
                >
                  {t.cancel}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Input Area */}
        {!isClosed ? (
          <div className="p-6 border-t border-mmg-mine-shaft">
            {/* Action Buttons (Client only) */}
            {canTakeAction && (
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => handleAction('accept')}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-heading text-sm uppercase hover:bg-green-700"
                >
                  <CheckCircle size={16} /> {t.accept}
                </button>
                <button
                  onClick={() => handleAction('request_changes')}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white font-heading text-sm uppercase hover:bg-orange-700"
                >
                  <AlertCircle size={16} /> {t.requestChanges}
                </button>
                <button
                  onClick={() => handleAction('reject')}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-heading text-sm uppercase hover:bg-red-700"
                >
                  <XCircle size={16} /> {t.reject}
                </button>
              </div>
            )}

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={t.typeMessage}
                className="flex-1 py-3 px-4 bg-black border border-mmg-mine-shaft text-white text-sm"
              />
              <button
                type="submit"
                disabled={sending || !newMessage.trim()}
                className="px-6 py-3 bg-white text-black font-heading text-sm uppercase hover:bg-black hover:text-white border border-white disabled:opacity-50 flex items-center gap-2"
              >
                <Send size={16} /> {t.send}
              </button>
            </form>
          </div>
        ) : (
          <div className="p-6 border-t border-mmg-mine-shaft text-center">
            <p className="text-mmg-silver-chalice">
              {t.proposalClosed.replace('{status}', proposal.status === 'accepted' ? t.accepted : t.rejected)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProposalDiscussion;
